import { z } from 'zod'
import {
  blockfrostMainnetOrigin,
  isAllowedBlockfrostUrl,
} from './blockfrostUrl'

/** Blockfrost の失敗を status ごとに判別して同期中断・再開に利用する。 */
export class BlockfrostError extends Error {
  readonly status: number
  readonly retryAt?: number

  /**
   * 上流の status のみを保持し、認証情報やリクエストヘッダーを含めない。
   * @param params.status 上流 HTTP status
   */
  constructor(params: { status: number; retryAt?: number }) {
    super(
      params.status === 402
        ? 'Blockfrost の日次上限に達しました (402)。次の UTC 日に同期を再開します。'
        : `Blockfrost request failed (${params.status})`,
    )
    this.name = 'BlockfrostError'
    this.status = params.status
    this.retryAt = params.retryAt
  }
}

/** 認証付き Worker GET proxy を経由する Blockfrost 通信の設定。 */
export type BlockfrostClientParameters = {
  readonly proxyEndpoint: string
  readonly getToken: () => Promise<string | null>
  readonly fetch?: typeof fetch
  readonly sleep?: (params: { milliseconds: number }) => Promise<void>
}

/**
 * ブラウザに project_id を持たせず、既存認証 proxy 経由でデータを取得する。
 * @param params Worker proxy の URL と Clerk token 取得関数、テスト用通信依存
 * @returns Zod 検証付き GET とページ単位の保存を待機する list 操作
 */
export const createBlockfrostClient = (params: BlockfrostClientParameters) => {
  const fetcher = params.fetch ?? fetch
  const sleep =
    params.sleep ??
    (({ milliseconds }: { milliseconds: number }) =>
      new Promise<void>((resolve) => setTimeout(resolve, milliseconds)))

  /**
   * データを検証して取得する。429 のみ最大 3 回再試行し、quota/ban は即停止する。
   * @param request API パスとレスポンス schema
   * @returns 検証済み応答
   */
  const get = async <T>(request: {
    path: string
    schema: z.ZodType<T, z.ZodTypeDef, unknown>
  }): Promise<T> => {
    const target = `${blockfrostMainnetOrigin}/api/v0${request.path}`
    if (!isAllowedBlockfrostUrl({ url: target })) {
      throw new Error('Unsupported Blockfrost path')
    }
    const proxyUrl = new URL(params.proxyEndpoint)
    proxyUrl.searchParams.set('url', target)
    for (let attempt = 0; ; attempt++) {
      const token = await params.getToken()
      if (!token) throw new Error('Cardano 同期にはログインが必要です')
      const response = await fetcher(proxyUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        redirect: 'error',
      })
      if (response.status === 429 && attempt < 3) {
        const retryAfter = response.headers.get('Retry-After')
        const seconds = retryAfter === null ? Number.NaN : Number(retryAfter)
        const retryAt =
          retryAfter === null ? Number.NaN : Date.parse(retryAfter)
        const delay = Number.isFinite(seconds)
          ? seconds * 1000
          : Number.isFinite(retryAt)
            ? retryAt - Date.now()
            : 1000 * 2 ** attempt
        // 極端に長い待機が必要な場合は同期を中断し、次回へ持ち越す。
        await response.body?.cancel()
        if (delay > 60_000)
          throw new BlockfrostError({
            status: 429,
            retryAt: Date.now() + delay,
          })
        await sleep({ milliseconds: Math.max(1000 * 2 ** attempt, delay) })
        continue
      }
      if (!response.ok) {
        await response.body?.cancel()
        throw new BlockfrostError({ status: response.status })
      }
      return request.schema.parse(await response.json())
    }
  }

  /**
   * 昇順のページを取得し、保存完了後にのみ次ページへ進む。
   * @param request.path ページクエリを含まない一覧パス
   * @param request.schema 一覧の各要素の実行時 schema
   * @param request.page 永続化済み checkpoint の次のページ（初回は 1）
   * @param request.onPage 完全に検証済みのページと次回 checkpoint の保存処理
   * @returns 空の終端ページも保存し終えたときに完了する Promise
   */
  const list = async <T>(request: {
    path: string
    schema: z.ZodType<T, z.ZodTypeDef, unknown>
    page?: number
    /** 未使用Addressの不存在のみ空の先頭ページとして扱う。途中ページの404は失敗する。 */
    emptyOn404?: boolean
    onPage: (params: {
      items: readonly T[]
      page: number
      complete: boolean
    }) => Promise<void>
  }): Promise<void> => {
    let page = z
      .number()
      .int()
      .positive()
      .safe()
      .parse(request.page ?? 1)
    if (request.path.includes('?'))
      throw new Error('Unexpected query in list path')
    for (;;) {
      const items = await get({
        path: `${request.path}?count=100&page=${page}&order=asc`,
        schema: z.array(request.schema).max(100),
      }).catch((error: unknown): T[] => {
        if (
          request.emptyOn404 &&
          page === 1 &&
          error instanceof BlockfrostError &&
          error.status === 404
        )
          return []
        throw error
      })
      const complete = items.length < 100
      await request.onPage({ items, page, complete })
      if (complete) return
      page++
    }
  }

  return { get, list }
}

/** Blockfrost 通信操作の型。 */
export type BlockfrostClient = ReturnType<typeof createBlockfrostClient>
